// =============================================================
// AUTOMATED TESTING BOT — engine
// =============================================================
//
// runSelfTests() executes a battery of checks across the whole platform and
// returns a structured report the Admin "Test Bot" panel renders. It covers:
//
//   • Store logic    — points/levels, drop-window timing, redemption guards,
//                       verification-method rules, code lookups, helpers.
//   • Database       — real Supabase round-trips (write → read → patch →
//                       delete) for users, catches, reviews and drops.
//   • Auth           — admin/brand credential config loads with the right shape.
//   • Connectivity   — the backend is reachable.
//
// Everything the bot writes is prefixed with SELF_TEST_PREFIX so it can never
// touch real data, and a purge runs at the end (and on failure) to clean up.
// =============================================================

import {
  levelForPoints,
  getDropWindowStatus,
  isDropActiveNow,
  findEventByCode,
  hasCollectedDrop,
  canReserveNewDrop,
  hasActiveReservation,
  normalizePhone,
  normalizeCode,
  generateDropCode,
  normalizeVerificationMethod,
  allowsCodeRedemption,
  allowsQrRedemption,
} from './store'
import type { Event, Reservation } from './store'
import {
  buildMockDrops,
  buildMockUsers,
  buildBrandQuestions,
  brandMeta,
} from './seed'
import {
  supabase,
  testSheetWrite,
  syncUser,
  fetchUserProfile,
  syncCatch,
  fetchUserCatches,
  syncReview,
  fetchAdminSnapshot,
  syncDrop,
  patchDrop,
  deleteDropRow,
  fetchDrops,
  fetchRecurringQuestions,
  fetchPartnerCodes,
  fetchAdminAuth,
  purgeSelfTestData,
  SELF_TEST_PREFIX,
} from './supabase'

export interface TestResult {
  name: string
  group: string
  passed: boolean
  /** Extra context shown for failures (and sometimes passes). */
  detail?: string
}

export interface TestReport {
  results: TestResult[]
  passed: number
  failed: number
  total: number
  startedAt: number
  finishedAt: number
  durationMs: number
}

export type TestProgress = (
  done: number,
  total: number,
  current: string,
) => void

type Check = {
  name: string
  group: string
  run: () =>
    | boolean
    | Promise<boolean>
    | { passed: boolean; detail?: string }
    | Promise<{ passed: boolean; detail?: string }>
}

const sentinelPhone = `${SELF_TEST_PREFIX}phone_${Date.now()}`
const sentinelResId = `${SELF_TEST_PREFIX}res_${Date.now()}`
const sentinelReviewId = `${SELF_TEST_PREFIX}rev_${Date.now()}`
const sentinelDropId = `${SELF_TEST_PREFIX}drop_${Date.now()}`
const sentinelDropCode = `DROP-ST${Math.floor(Math.random() * 9000 + 1000)}`
const sentinelDropId2 = `${SELF_TEST_PREFIX}dropq_${Date.now()}`
const sentinelDropCode2 = `DROP-SQ${Math.floor(Math.random() * 9000 + 1000)}`

/** Build a synthetic event for window/lookup tests. */
function makeEvent(partial: Partial<Event>): Event {
  return {
    id: 'e1',
    code: 'DROP-TEST',
    title: 'Test Drop',
    brand: 'TestBrand',
    campaign: 'Test Campaign',
    verificationMethod: 'both',
    description: '',
    date: '',
    time: '',
    address: '',
    boxesLeft: 10,
    totalBoxes: 10,
    theme: 'Drop',
    type: 'reservation',
    requiredPoints: 0,
    ...partial,
  }
}

const todayISO = () => new Date().toISOString().slice(0, 10)

const STORE_CHECKS: Check[] = [
  {
    name: 'Level: 0 pts → Lil Minnow',
    group: 'Store logic',
    run: () => levelForPoints(0) === 'Lil Minnow',
  },
  {
    name: 'Level: 30 pts → Hooked',
    group: 'Store logic',
    run: () => levelForPoints(30) === 'Hooked',
  },
  {
    name: 'Level: 75 pts → Reel Rookie',
    group: 'Store logic',
    run: () => levelForPoints(75) === 'Reel Rookie',
  },
  {
    name: 'Level: 150 pts → Big Catch',
    group: 'Store logic',
    run: () => levelForPoints(150) === 'Big Catch',
  },
  {
    name: 'Level: 250 pts → Master Angler',
    group: 'Store logic',
    run: () => levelForPoints(250) === 'Master Angler',
  },
  {
    name: 'Level: 1000 pts → Master Angler',
    group: 'Store logic',
    run: () => levelForPoints(1000) === 'Master Angler',
  },
  {
    name: 'Level: just below a tier stays on the lower tier',
    group: 'Store logic',
    run: () =>
      levelForPoints(29) === 'Lil Minnow' && levelForPoints(74) === 'Hooked',
  },
  {
    name: 'normalizePhone strips spaces',
    group: 'Store logic',
    run: () => normalizePhone('070 123 45 67') === '0701234567',
  },
  {
    name: 'normalizeCode upper-cases & trims',
    group: 'Store logic',
    run: () => normalizeCode(' drop-7k2m ') === 'DROP-7K2M',
  },
  {
    name: 'generateDropCode format DROP-XXXX',
    group: 'Store logic',
    run: () => /^DROP-[A-Z0-9]{4}$/.test(generateDropCode()),
  },
  {
    name: 'Drop window: all-day drop is active today',
    group: 'Store logic',
    run: () => {
      const ev = makeEvent({ date: todayISO(), time: '' })
      return getDropWindowStatus(ev) === 'active' && isDropActiveNow(ev)
    },
  },
  {
    name: 'Drop window: future date → before',
    group: 'Store logic',
    run: () => {
      const future = new Date(Date.now() + 3 * 86400000)
        .toISOString()
        .slice(0, 10)
      return getDropWindowStatus(makeEvent({ date: future })) === 'before'
    },
  },
  {
    name: 'Drop window: past date → after',
    group: 'Store logic',
    run: () => {
      const past = new Date(Date.now() - 3 * 86400000)
        .toISOString()
        .slice(0, 10)
      return getDropWindowStatus(makeEvent({ date: past })) === 'after'
    },
  },
  {
    name: 'findEventByCode matches case-insensitively',
    group: 'Store logic',
    run: () => {
      const events = [makeEvent({ id: 'a', code: 'DROP-AB12' })]
      return findEventByCode(events, 'drop-ab12')?.id === 'a'
    },
  },
  {
    name: 'findEventByCode rejects unknown code',
    group: 'Store logic',
    run: () => findEventByCode([makeEvent({})], 'NOPE-9999') === undefined,
  },
  {
    name: 'hasCollectedDrop true after redeem',
    group: 'Store logic',
    run: () => {
      const res: Reservation[] = [
        {
          id: 'r1',
          eventId: 'e1',
          userId: '0701234567',
          status: 'redeemed',
          timestamp: Date.now(),
          userCode: 'A',
          staffCode: 'B',
          batchCode: 'X',
        },
      ]
      return (
        hasCollectedDrop(res, 'e1', '0701234567') === true &&
        hasCollectedDrop(res, 'e2', '0701234567') === false
      )
    },
  },
  {
    name: 'canReserveNewDrop blocked by active reservation',
    group: 'Store logic',
    run: () => {
      const res: Reservation[] = [
        {
          id: 'r1',
          eventId: 'e1',
          userId: 'u',
          status: 'reserved',
          timestamp: Date.now(),
          userCode: 'A',
          staffCode: 'B',
          batchCode: 'X',
        },
      ]
      return hasActiveReservation(res) && canReserveNewDrop(res) === false
    },
  },
  {
    name: 'Verification: normalize defaults to both',
    group: 'Store logic',
    run: () =>
      normalizeVerificationMethod('qr') === 'qr' &&
      normalizeVerificationMethod('code') === 'code' &&
      normalizeVerificationMethod(undefined) === 'both',
  },
  {
    name: 'Verification: QR-only blocks code, allows QR',
    group: 'Store logic',
    run: () =>
      allowsCodeRedemption('qr') === false && allowsQrRedemption('qr') === true,
  },
  {
    name: 'Verification: Code-only blocks QR, allows code',
    group: 'Store logic',
    run: () =>
      allowsQrRedemption('code') === false &&
      allowsCodeRedemption('code') === true,
  },
  {
    name: 'Verification: Both allows QR and code',
    group: 'Store logic',
    run: () =>
      allowsQrRedemption('both') === true &&
      allowsCodeRedemption('both') === true,
  },
  {
    name: 'Verification: invalid input normalizes to both',
    group: 'Store logic',
    run: () =>
      normalizeVerificationMethod('garbage') === 'both' &&
      normalizeVerificationMethod(null) === 'both',
  },
  {
    name: 'canReserveNewDrop allowed with no active reservation',
    group: 'Store logic',
    run: () =>
      hasActiveReservation([]) === false && canReserveNewDrop([]) === true,
  },
]

// Pure-function checks for the mock-data generator (no DB writes — safe).
const SEED_CHECKS: Check[] = [
  {
    name: 'Mock drops: campaigns × brands count',
    group: 'Mock data',
    run: () => {
      const drops = buildMockDrops({
        campaigns: 3,
        brandsPerCampaign: 2,
        boxesPerDrop: 50,
        verificationMethod: 'qr',
        questionsPerDrop: 3,
      })
      const campaigns = new Set(drops.map((d) => d.campaign)).size
      return {
        passed: drops.length === 6 && campaigns === 3,
        detail: `${drops.length} drops across ${campaigns} campaigns`,
      }
    },
  },
  {
    name: 'Mock drops: config applied (method, boxes, questions)',
    group: 'Mock data',
    run: () => {
      const drops = buildMockDrops({
        campaigns: 2,
        brandsPerCampaign: 2,
        boxesPerDrop: 50,
        verificationMethod: 'qr',
        questionsPerDrop: 3,
      })
      const ok = drops.every(
        (d) =>
          d.verificationMethod === 'qr' &&
          d.totalBoxes === 50 &&
          d.boxesLeft === 50 &&
          (d.customQuestions?.length ?? 0) === 3,
      )
      return { passed: ok, detail: ok ? 'all drops match config' : 'mismatch' }
    },
  },
  {
    name: 'Mock drops: brand socials & website populated',
    group: 'Mock data',
    run: () => {
      const drops = buildMockDrops({ campaigns: 1, brandsPerCampaign: 3 })
      const ok = drops.every(
        (d) => !!d.instagramUrl && !!d.websiteUrl && !!d.brandLogo,
      )
      return {
        passed: ok,
        detail: ok ? 'socials/website/logo set' : 'missing brand assets',
      }
    },
  },
  {
    name: 'brandMeta derives handle & website from name',
    group: 'Mock data',
    run: () => {
      const m = brandMeta('Vitamin Well')
      const ok =
        m.instagramUrl.includes('vitaminwell') &&
        m.websiteUrl.includes('vitaminwell')
      return { passed: ok, detail: `${m.instagramUrl} / ${m.websiteUrl}` }
    },
  },
  {
    name: 'buildBrandQuestions returns the requested count (capped)',
    group: 'Mock data',
    run: () => {
      const three = buildBrandQuestions('NOCCO', 3)
      const capped = buildBrandQuestions('NOCCO', 99)
      return {
        passed: three.length === 3 && capped.length <= 5 && capped.length >= 3,
        detail: `3→${three.length}, 99→${capped.length}`,
      }
    },
  },
  {
    name: 'Mock users: count + full funnel produces catches & reviews',
    group: 'Mock data',
    run: () => {
      const drops = buildMockDrops({ campaigns: 1, brandsPerCampaign: 2 })
      const batch = buildMockUsers(drops, {
        count: 12,
        redeemRate: 1,
        reviewRate: 1,
      })
      const ok =
        batch.users.length === 12 &&
        batch.catches.length === 12 &&
        batch.reviews.length > 0
      return {
        passed: ok,
        detail: `${batch.users.length} users, ${batch.catches.length} catches, ${batch.reviews.length} reviews`,
      }
    },
  },
  {
    name: 'Mock users: zero redeem rate yields no reviews',
    group: 'Mock data',
    run: () => {
      const drops = buildMockDrops({ campaigns: 1, brandsPerCampaign: 1 })
      const batch = buildMockUsers(drops, {
        count: 10,
        redeemRate: 0,
        reviewRate: 1,
      })
      const allReserved = batch.catches.every((c) => c.status === 'reserved')
      return {
        passed: allReserved && batch.reviews.length === 0,
        detail: `reviews=${batch.reviews.length}, allReserved=${allReserved}`,
      }
    },
  },
  {
    name: 'Mock users: city filter restricts the pool',
    group: 'Mock data',
    run: () => {
      const drops = buildMockDrops({ campaigns: 1, brandsPerCampaign: 1 })
      const batch = buildMockUsers(drops, {
        count: 15,
        cities: ['Malmö'],
      })
      const ok = batch.users.every((u) => u.city === 'Malmö')
      return {
        passed: ok,
        detail: ok ? 'all users in Malmö' : 'city filter leaked',
      }
    },
  },
]

const DB_CHECKS: Check[] = [
  {
    name: 'Backend reachable',
    group: 'Connectivity',
    run: async () => {
      const r = await testSheetWrite()
      return { passed: r.ok, detail: r.body }
    },
  },
  {
    name: 'User: write → read back with points',
    group: 'Database',
    run: async () => {
      const ok = await syncUser({
        name: 'Self Test User',
        phone: sentinelPhone,
        points: 42,
        level: levelForPoints(42),
        catchCount: 1,
        city: 'Testville',
      })
      if (!ok) return { passed: false, detail: 'syncUser returned false' }
      const profile = await fetchUserProfile(sentinelPhone)
      if (!profile)
        return { passed: false, detail: 'profile not found after write' }
      return {
        passed: profile.user.points === 42 && profile.user.city === 'Testville',
        detail: `points=${profile.user.points}, city=${profile.user.city}`,
      }
    },
  },
  {
    name: 'Catch: write → read back by phone',
    group: 'Database',
    run: async () => {
      const ok = await syncCatch({
        reservationId: sentinelResId,
        userPhone: sentinelPhone,
        event: 'Self Test Drop',
        brand: 'TestBrand',
        batchCode: 'ST',
        round: 1,
        status: 'reserved',
        userCode: 'STUC',
        staffCode: 'STSC',
      })
      if (!ok) return { passed: false, detail: 'syncCatch returned false' }
      const catches = await fetchUserCatches(sentinelPhone)
      const found = catches.find((c) => c.reservationId === sentinelResId)
      return {
        passed: !!found && found.status === 'reserved',
        detail: found ? `status=${found.status}` : 'catch not found',
      }
    },
  },
  {
    name: 'Review: write → appears in snapshot',
    group: 'Database',
    run: async () => {
      const ok = await syncReview({
        reviewId: sentinelReviewId,
        reservationId: sentinelResId,
        userPhone: sentinelPhone,
        product: 'Test Product',
        brand: 'TestBrand',
        taste: true,
        packaging: true,
        buyAgain: true,
      })
      if (!ok) return { passed: false, detail: 'syncReview returned false' }
      const snap = await fetchAdminSnapshot()
      const found = snap.reviews.find((r) => r.id === sentinelReviewId)
      return {
        passed: !!found,
        detail: found ? 'found in snapshot' : 'missing',
      }
    },
  },
  {
    name: 'Drop: write → read → campaign & verification round-trip',
    group: 'Database',
    run: async () => {
      const ok = await syncDrop({
        id: sentinelDropId,
        code: sentinelDropCode,
        title: 'Self Test Drop',
        brand: 'TestBrand',
        campaign: 'Self Test Campaign',
        verificationMethod: 'qr',
        description: 'temp',
        date: todayISO(),
        time: '12:00 - 14:00',
        address: 'Test',
        requiredPoints: 0,
        totalBoxes: 5,
        boxesLeft: 5,
        active: true,
      })
      if (!ok) return { passed: false, detail: 'syncDrop returned false' }
      const drops = await fetchDrops()
      const found = drops?.find((d) => d.id === sentinelDropId)
      if (!found) return { passed: false, detail: 'drop not found after write' }
      return {
        passed:
          found.campaign === 'Self Test Campaign' &&
          found.verificationMethod === 'qr',
        detail: `campaign=${found.campaign}, method=${found.verificationMethod}`,
      }
    },
  },
  {
    name: 'Drop: patch updates a field',
    group: 'Database',
    run: async () => {
      await patchDrop(sentinelDropId, {
        boxesLeft: 3,
        verificationMethod: 'both',
      })
      const drops = await fetchDrops()
      const found = drops?.find((d) => d.id === sentinelDropId)
      return {
        passed:
          !!found &&
          found.boxesLeft === 3 &&
          found.verificationMethod === 'both',
        detail: found
          ? `boxesLeft=${found.boxesLeft}, method=${found.verificationMethod}`
          : 'missing',
      }
    },
  },
  {
    name: 'Drop: delete removes it',
    group: 'Database',
    run: async () => {
      await deleteDropRow(sentinelDropId)
      const drops = await fetchDrops()
      const stillThere = drops?.some((d) => d.id === sentinelDropId)
      return {
        passed: !stillThere,
        detail: stillThere ? 'still present' : 'deleted',
      }
    },
  },
  {
    name: 'User: custom answers round-trip',
    group: 'Database',
    run: async () => {
      const ok = await syncUser({
        name: 'Self Test User',
        phone: sentinelPhone,
        points: 42,
        level: levelForPoints(42),
        catchCount: 1,
        customAnswers: { st_q1: 'Citrus', st_q2: 'Instagram' },
      })
      if (!ok) return { passed: false, detail: 'syncUser returned false' }
      const profile = await fetchUserProfile(sentinelPhone)
      const a = profile?.user.customAnswers || {}
      return {
        passed: a.st_q1 === 'Citrus' && a.st_q2 === 'Instagram',
        detail: `answers=${JSON.stringify(a)}`,
      }
    },
  },
  {
    name: 'Drop: custom questions round-trip',
    group: 'Database',
    run: async () => {
      const ok = await syncDrop({
        id: sentinelDropId2,
        code: sentinelDropCode2,
        title: 'Self Test Questions Drop',
        brand: 'TestBrand',
        campaign: 'Self Test Campaign',
        verificationMethod: 'both',
        description: 'temp',
        date: todayISO(),
        time: '12:00 - 14:00',
        address: 'Test',
        requiredPoints: 0,
        totalBoxes: 5,
        boxesLeft: 5,
        active: true,
        questions: JSON.stringify([
          {
            id: 'st_q_flavor',
            label: 'Favorite flavor?',
            type: 'choice',
            options: ['Citrus', 'Berry'],
          },
        ]),
      })
      if (!ok) return { passed: false, detail: 'syncDrop returned false' }
      const drops = await fetchDrops()
      const found = drops?.find((d) => d.id === sentinelDropId2)
      const qs = found?.customQuestions || []
      await deleteDropRow(sentinelDropId2)
      return {
        passed: qs.length === 1 && qs[0].id === 'st_q_flavor',
        detail: `${qs.length} question(s) read back`,
      }
    },
  },
  {
    name: 'Config: recurring questions readable',
    group: 'Database',
    run: async () => {
      const q = await fetchRecurringQuestions()
      return {
        passed: Array.isArray(q),
        detail: q ? `${q.length} questions` : 'null (read failed)',
      }
    },
  },
  {
    name: 'Config: partner codes readable',
    group: 'Database',
    run: async () => {
      const c = await fetchPartnerCodes()
      return {
        passed: Array.isArray(c),
        detail: c ? `${c.length} codes` : 'null (read failed)',
      }
    },
  },
  {
    name: 'Auth: admin credentials config loads',
    group: 'Auth',
    run: async () => {
      const cfg = await fetchAdminAuth()
      return {
        passed:
          typeof cfg.adminPassword === 'string' &&
          cfg.adminPassword.length > 0 &&
          typeof cfg.brandPassword === 'string' &&
          cfg.brandPassword.length > 0,
        detail: 'admin & brand passwords present',
      }
    },
  },
  {
    name: 'Tables: users/catches/reviews/drops/app_config exist',
    group: 'Database',
    run: async () => {
      const tables = ['users', 'catches', 'reviews', 'drops', 'app_config']
      const missing: string[] = []
      for (const t of tables) {
        const { error } = await supabase
          .from(t)
          .select('*', { count: 'exact', head: true })
        if (error) missing.push(`${t} (${error.message})`)
      }
      return {
        passed: missing.length === 0,
        detail: missing.length
          ? `Missing/blocked: ${missing.join(', ')}`
          : 'all reachable',
      }
    },
  },
]

/**
 * Run the full suite. `onProgress` fires after each test so the UI can show a
 * live bar. The bot cleans up its sentinel rows whether it passes or fails.
 */
export async function runSelfTests(
  onProgress?: TestProgress,
): Promise<TestReport> {
  const checks = [...STORE_CHECKS, ...SEED_CHECKS, ...DB_CHECKS]
  const total = checks.length
  const results: TestResult[] = []
  const startedAt = Date.now()

  for (let i = 0; i < checks.length; i++) {
    const check = checks[i]
    onProgress?.(i, total, check.name)
    try {
      const outcome = await check.run()
      const normalized =
        typeof outcome === 'boolean' ? { passed: outcome } : outcome
      results.push({
        name: check.name,
        group: check.group,
        passed: normalized.passed,
        detail: normalized.detail,
      })
    } catch (err) {
      results.push({
        name: check.name,
        group: check.group,
        passed: false,
        detail: `Threw: ${String(err)}`,
      })
    }
    onProgress?.(i + 1, total, check.name)
  }

  // Always clean up sentinel rows, even if a DB test failed midway.
  try {
    await purgeSelfTestData()
  } catch {
    /* best-effort */
  }

  const finishedAt = Date.now()
  const passed = results.filter((r) => r.passed).length
  const failed = results.length - passed
  return {
    results,
    passed,
    failed,
    total: results.length,
    startedAt,
    finishedAt,
    durationMs: finishedAt - startedAt,
  }
}

/** Build a copy-pasteable plain-text report (handy for sharing failures). */
export function formatReport(report: TestReport): string {
  const lines: string[] = []
  lines.push(
    `Gone Fishing — Self-Test Report (${new Date(report.startedAt).toLocaleString()})`,
  )
  lines.push(
    `${report.passed}/${report.total} passed • ${report.failed} failed • ${report.durationMs}ms`,
  )
  lines.push('')
  const groups = Array.from(new Set(report.results.map((r) => r.group)))
  for (const group of groups) {
    lines.push(`## ${group}`)
    for (const r of report.results.filter((x) => x.group === group)) {
      lines.push(
        `  ${r.passed ? '✓' : '✗'} ${r.name}${r.detail ? ` — ${r.detail}` : ''}`,
      )
    }
    lines.push('')
  }
  return lines.join('\n')
}
